import { useContext } from "react";
import { OBJECT_TYPE } from "common/constants";
import { getObjectDisplay } from "common-client/utils/customObject";
import { useDeletePropertyMutation } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import { useDeleteModal } from "../../Common/DeleteModal";

export interface UseDeletePropertyModalProps {
  property: {
    id: string;
    latitude: number;
    longitude: number;
    fullAddress?: Maybe<string>;
    streetAddress?: Maybe<string>;
    city?: Maybe<string>;
  };
  refreshMap: () => void;
  closeModal: () => void;
  onDelete: () => void;
}

export const useDeletePropertyModal = ({
  property,
  refreshMap,
  onDelete,
}: UseDeletePropertyModalProps) => {
  const { account } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const [deleteProperty, { loading }] = useDeletePropertyMutation({
    onCompleted: () => {
      track("Deleted Property", {
        streetAddress: property.streetAddress,
        latitude: property.latitude,
        longitude: property.longitude,
        city: property.city,
      });

      refreshMap();
      addSuccessToast("The property was successfully deleted");
      onDelete();
    },
    onError: () => {
      addErrorToast(
        "There was an error deleting this property. Please try again. If the problem persists, please email support@withforerunner.com",
      );
    },
  });

  const confirmations = [
    "All associated data with the property will be deleted.",
    "The property will be removed from the dashboard for all users.",
    ...(account?.publicPortal.enabled
      ? ["The property will no longer be accessible from your public website."]
      : []),
  ];

  const subheaderText = getObjectDisplay({
    objectType: OBJECT_TYPE.PROPERTY,
    object: property,
  });

  const [showDeletePropertyModal, hideDeletePropertyModal] = useDeleteModal({
    headerText: "Delete property",
    subheaderText,
    confirmations,
    confirmationPreamble:
      "By deleting the selected property, you confirm that:",
    size: "large",
    deleteButtonText: "Delete property",
    loading,
    onDelete: async () => {
      await deleteProperty({
        variables: { propertyId: property.id },
      });
    },
    dependencies: [property, loading],
  });

  return [showDeletePropertyModal, hideDeletePropertyModal] as const;
};
